import { useState, useEffect } from 'react';
import { useI18n } from '../../../../i18n';
import { useAuth } from '../../../../context/AuthContext';
import { AdminCard } from '../components/AdminCard';
import { AdminStatCard } from '../components/AdminStatCard';
import { AdminBadge } from '../components/AdminBadge';
import { AdminBar } from '../components/AdminBar';
import { LoadingSpinner } from '../../../shared/components/LoadingSpinner';
import { InfoTooltip } from '@/components/ui/InfoTooltip';
import { adminRequest } from '../../../shared/services/api';

// ===============================================================
// AdminDashboard — Tab "Panel General": CEO-approved visual
// ===============================================================

interface DashboardStats {
  totalUsuarios: number;
  totalDocumentos: number;
  totalPublicados: number;
  totalProcesos: number;
  totalModulos: number;
}

// 0587 fix #2: backend devuelve `accion` y `tipoEntidad` (campos reales del modelo
// RegistroAuditoria), no `evento`/`entidad`. Mismatch hist. que producia badges
// vacios y columna entidad en blanco.
interface AuditEntry {
  id: string;
  accion: string;
  tipoEntidad: string;
  entidadId?: string;
  creadoEn: string;
  datosAnteriores?: Record<string, unknown> | null;
  datosNuevos?: Record<string, unknown> | null;
  usuario: { nombreCompleto: string; email: string } | null;
}

interface SystemHealth {
  database: 'ok' | 'error';
  dbLatency?: number;
  meilisearch: 'ok' | 'error' | 'not_configured';
  meiliLatency?: number;
  storage: 'ok' | 'error' | 'not_configured';
  auth: 'ok';
  ia: 'ok' | 'not_configured';
  totalLatency?: number;
}

// 0571: stats arrancan en 0; mostramos valores reales o "—" — sin fallback fake
const EMPTY_STATS: DashboardStats = {
  totalUsuarios: 0,
  totalDocumentos: 0,
  totalPublicados: 0,
  totalProcesos: 0,
  totalModulos: 0,
};

const ACTION_COLORS: Record<string, { bg: string; color: string }> = {
  CREAR: { bg: '#DCFCE7', color: '#15803D' },
  ACTUALIZAR: { bg: '#FEF3C7', color: '#92400E' },
  ELIMINAR: { bg: '#FEE2E2', color: '#991B1B' },
  PUBLICAR: { bg: '#DBEAFE', color: '#1E40AF' },
};

export function AdminDashboard() {
  const { t } = useI18n();
  const { isPlatformAdmin } = useAuth();
  const [stats, setStats] = useState<DashboardStats>(EMPTY_STATS);
  // 0582: tenant para max real (maxUsuarios, plan) en lugar de hardcoded /999
  const [tenantInfo, setTenantInfo] = useState<{
    maxUsuarios: number;
    plan: string;
  } | null>(null);
  const [activity, setActivity] = useState<AuditEntry[]>([]);
  const [auditLog, setAuditLog] = useState<AuditEntry[]>([]);
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [filterUser, setFilterUser] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [loading, setLoading] = useState(true);
  const [reindexing, setReindexing] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: 'ok' | 'warn' } | null>(null);

  const showToast = (msg: string, type: 'ok' | 'warn' = 'ok') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const [statsRes, activityRes, auditRes, healthRes, tenantRes] = await Promise.allSettled([
        adminRequest<unknown>('/dashboard/stats'),
        adminRequest<unknown>('/audit-log?pageSize=5'),
        adminRequest<unknown>('/audit-log?pageSize=20'),
        adminRequest<{ data: SystemHealth }>('/system-health'),
        // 0582: tenant para maxUsuarios + plan reales
        adminRequest<{ data: { maxUsuarios: number; plan: string } }>('/tenant'),
      ]);

      if (
        tenantRes.status === 'fulfilled' &&
        (tenantRes.value as { data?: { maxUsuarios: number; plan: string } } | null)?.data
      ) {
        const t = (tenantRes.value as { data: { maxUsuarios: number; plan: string } }).data;
        setTenantInfo({ maxUsuarios: t.maxUsuarios ?? 5, plan: t.plan ?? 'BASIC' });
      }

      if (healthRes.status === 'fulfilled' && (healthRes.value as { data?: SystemHealth })?.data) {
        setHealth((healthRes.value as { data: SystemHealth }).data);
      }

      if (
        statsRes.status === 'fulfilled' &&
        (statsRes.value as { data?: DashboardStats } | null)?.data
      ) {
        const d = (statsRes.value as { data: DashboardStats }).data;
        setStats({
          totalUsuarios: d.totalUsuarios ?? 0,
          totalDocumentos: d.totalDocumentos ?? 0,
          totalPublicados: d.totalPublicados ?? 0,
          totalProcesos: d.totalProcesos ?? 0,
          totalModulos: d.totalModulos ?? 0,
        });
      } else {
        try {
          const fallback = (await adminRequest<unknown>('/tenant/stats')) as {
            data?: Partial<DashboardStats>;
          } | null;
          const fbData = fallback?.data;
          if (fbData) {
            setStats((prev) => ({
              ...prev,
              totalUsuarios: fbData.totalUsuarios ?? prev.totalUsuarios,
              totalDocumentos: fbData.totalDocumentos ?? prev.totalDocumentos,
              totalProcesos: fbData.totalProcesos ?? prev.totalProcesos,
            }));
          }
        } catch {
          /* fallback stays */
        }
      }

      if (
        activityRes.status === 'fulfilled' &&
        (activityRes.value as { data?: AuditEntry[] } | null)?.data
      ) {
        const entries = (activityRes.value as { data: AuditEntry[] }).data.slice(0, 5);
        if (entries.length > 0) setActivity(entries);
      }

      if (
        auditRes.status === 'fulfilled' &&
        (auditRes.value as { data?: AuditEntry[] } | null)?.data
      ) {
        const entries = (auditRes.value as { data: AuditEntry[] }).data;
        if (entries.length > 0) setAuditLog(entries);
      }
    } catch {
      /* fallback data already set */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <LoadingSpinner text={t.common.cargando} />
      </div>
    );
  }

  // 0571: filtered audit log — sin fallback fake, mostrar vacio si no hay datos
  const filteredAudit = auditLog.filter((e) => {
    if (
      filterUser &&
      !(e.usuario?.nombreCompleto ?? '').toLowerCase().includes(filterUser.toLowerCase())
    )
      return false;
    if (filterAction && e.accion !== filterAction) return false;
    return true;
  });

  // 0571: descarga CSV del audit log via blob
  const handleExportCsv = async () => {
    setExportingCsv(true);
    try {
      const params = new URLSearchParams();
      if (filterUser) params.set('usuario', filterUser);
      if (filterAction) params.set('accion', filterAction);
      const token = localStorage.getItem('vianet-token');
      const res = await fetch(`/api/v1/admin/audit-log/export.csv?${params.toString()}`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `auditoria-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showToast('CSV descargado correctamente');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Error al exportar CSV', 'warn');
    } finally {
      setExportingCsv(false);
    }
  };

  // 0571: dispara reindexacion Meilisearch (solo SUPERADMIN_PLATFORM)
  const handleReindex = async () => {
    if (!isPlatformAdmin) {
      showToast('Solo SUPERADMIN_PLATFORM puede reindexar', 'warn');
      return;
    }
    if (!confirm('¿Disparar reindexacion completa de Meilisearch? Operacion costosa.')) return;
    setReindexing(true);
    try {
      const res = await adminRequest<{
        data: { status: string; message: string; duration: number };
      }>('/system/reindex-search', { method: 'POST' });
      showToast(
        `${res.data.status}: ${res.data.message} (${res.data.duration}ms)`,
        res.data.status === 'triggered' ? 'ok' : 'warn',
      );
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Error al reindexar', 'warn');
    } finally {
      setReindexing(false);
    }
  };

  const statCards = [
    {
      label: t.admin.dashboard.totalUsuarios,
      // 0582: maxUsuarios real del tenant en lugar de "/5" hardcoded
      value: `${stats.totalUsuarios}/${tenantInfo?.maxUsuarios ?? '-'}`,
      accent: '#33BCE1',
    },
    { label: t.admin.dashboard.totalDocumentos, value: stats.totalDocumentos, accent: '#2F5496' },
    {
      label: t.admin.dashboard.publicados ?? 'Publicados',
      value: stats.totalPublicados,
      accent: '#15803D',
    },
    { label: t.admin.dashboard.totalProcesos, value: stats.totalProcesos, accent: '#16507D' },
    { label: t.admin.dashboard.modulos ?? 'Modulos', value: stats.totalModulos, accent: '#1A94BF' },
  ];

  const badgeFor = (evento: string) => {
    const c = ACTION_COLORS[evento] ?? { bg: '#F3F4F6', color: '#374151' };
    return (
      <AdminBadge bg={c.bg} color={c.color}>
        {evento}
      </AdminBadge>
    );
  };

  const fmtDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Header con tooltip */}
      <h1
        style={{
          fontSize: 18,
          fontWeight: 700,
          color: '#1F3864',
          margin: 0,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        {t.admin.dashboardTitle}
        <InfoTooltip
          title={t.admin.tooltips.dashboard.titulo}
          text={t.admin.tooltips.dashboard.ayuda}
        />
      </h1>

      {/* ── Block 1: Stat cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 14 }}>
        {statCards.map((s) => (
          <AdminStatCard key={s.label} label={s.label} value={s.value} accent={s.accent} />
        ))}
      </div>

      {/* ── Block 2: Plan + Activity ── */}
      <div style={{ display: 'flex', gap: 14 }}>
        {/* Plan y uso */}
        <AdminCard
          accent="#33BCE1"
          title={t.admin.dashboard.planYUso ?? 'Plan y uso'}
          style={{ flex: 1 }}
        >
          {/* 0582: badge plan + N modulos activos del tenant real */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <AdminBadge bg="#0E243E" color="#fff">
              {tenantInfo?.plan ?? '-'}
            </AdminBadge>
            <span style={{ fontSize: 11, color: '#595959' }}>
              {`${stats.totalModulos} ${t.admin.dashboard.modulos ?? 'modulos'}`}
            </span>
          </div>
          {/* 0587 fix #3: solo AdminBar Usuarios (cuota real via tenant.maxUsuarios).
              Documentos y Tokens IA NO se muestran porque no hay endpoint de cuota
              real - mostrar 99/100 como max=Math.max(value, 100) era enganoso (parece
              limite de plan cuando no lo es). Reactivar cuando exista /admin/tenant/usage. */}
          <AdminBar
            label={t.admin.dashboard.totalUsuarios}
            value={stats.totalUsuarios}
            max={tenantInfo?.maxUsuarios ?? Math.max(stats.totalUsuarios, 5)}
            color="#33BCE1"
          />
          {/* Stat plana de documentos sin barra (no es una cuota) */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '8px 0',
              fontSize: 12,
            }}
          >
            <span style={{ color: '#595959' }}>{t.admin.dashboard.totalDocumentos}</span>
            <span style={{ color: '#1F3864', fontWeight: 600 }}>{stats.totalDocumentos}</span>
          </div>
        </AdminCard>

        {/* Actividad reciente */}
        <AdminCard accent="#2F5496" title={t.admin.dashboard.actividadReciente} style={{ flex: 1 }}>
          {activity.length === 0 ? (
            <div style={{ color: '#595959', fontSize: 12, padding: '12px 0' }}>
              {t.admin.dashboard.sinActividad}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {activity.map((entry, idx) => (
                <div
                  key={entry.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '7px 0',
                    borderBottom: idx < activity.length - 1 ? '1px solid #F2F4F6' : 'none',
                  }}
                >
                  {badgeFor(entry.accion)}
                  <span
                    style={{
                      flex: 1,
                      fontSize: 12,
                      color: '#1F3864',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {entry.tipoEntidad}
                  </span>
                  <span
                    style={{
                      fontSize: 10,
                      color: '#595959',
                      fontFamily: 'monospace',
                      flexShrink: 0,
                    }}
                  >
                    {fmtDate(entry.creadoEn)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </AdminCard>
      </div>

      {/* ── Block 3: Auditoria ── */}
      <div>
        <h2 style={{ fontSize: 14, fontWeight: 600, color: '#0E243E', margin: '0 0 10px' }}>
          {t.admin.dashboard.auditoria ?? 'Auditoria'}
        </h2>
        {/* Filters */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 10, alignItems: 'center' }}>
          <input
            type="text"
            placeholder={t.admin.dashboard.filtrarUsuario ?? 'Filtrar usuario...'}
            value={filterUser}
            onChange={(e) => setFilterUser(e.target.value)}
            style={{
              padding: '6px 12px',
              fontSize: 12,
              border: '1px solid #D9D9D9',
              borderRadius: 4,
              outline: 'none',
              flex: 1,
              maxWidth: 220,
            }}
          />
          <select
            value={filterAction}
            onChange={(e) => setFilterAction(e.target.value)}
            style={{
              padding: '6px 10px',
              fontSize: 12,
              border: '1px solid #D9D9D9',
              borderRadius: 4,
              outline: 'none',
            }}
          >
            <option value="">{t.admin.dashboard.accion ?? 'Accion'}</option>
            <option value="CREAR">CREAR</option>
            <option value="ACTUALIZAR">ACTUALIZAR</option>
            <option value="ELIMINAR">ELIMINAR</option>
            <option value="PUBLICAR">PUBLICAR</option>
          </select>
          <button
            onClick={handleExportCsv}
            disabled={exportingCsv}
            style={{
              padding: '6px 16px',
              fontSize: 11,
              fontWeight: 600,
              background: exportingCsv ? '#9CA3AF' : '#2F5496',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              cursor: exportingCsv ? 'not-allowed' : 'pointer',
            }}
          >
            {exportingCsv ? 'Exportando...' : 'CSV'}
          </button>
        </div>
        {/* Table */}
        <div style={{ border: '1px solid #D9D9D9', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#0E243E' }}>
                {[
                  t.admin.dashboard.fecha ?? 'FECHA',
                  t.admin.dashboard.usuario ?? 'USUARIO',
                  t.admin.dashboard.accion ?? 'ACCION',
                  t.admin.dashboard.entidad ?? 'ENTIDAD',
                ].map((h) => (
                  <th
                    key={h}
                    style={{
                      padding: '8px 12px',
                      color: '#fff',
                      fontSize: 10,
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      textAlign: 'left',
                      letterSpacing: 0.5,
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredAudit.slice(0, 10).map((entry, idx) => (
                <tr key={entry.id} style={{ background: idx % 2 === 0 ? '#fff' : '#D6E4F0' }}>
                  <td
                    style={{
                      padding: '7px 12px',
                      fontSize: 11,
                      color: '#595959',
                      fontFamily: 'monospace',
                    }}
                  >
                    {fmtDate(entry.creadoEn)}
                  </td>
                  <td style={{ padding: '7px 12px', fontSize: 11, color: '#1F3864' }}>
                    {entry.usuario?.nombreCompleto ?? '—'}
                  </td>
                  <td style={{ padding: '7px 12px' }}>{badgeFor(entry.accion)}</td>
                  <td
                    style={{ padding: '7px 12px', fontSize: 11, fontWeight: 600, color: '#0E243E' }}
                  >
                    {entry.tipoEntidad}
                  </td>
                </tr>
              ))}
              {filteredAudit.length === 0 && (
                <tr>
                  <td
                    colSpan={4}
                    style={{
                      padding: '16px 12px',
                      textAlign: 'center',
                      color: '#595959',
                      fontSize: 12,
                    }}
                  >
                    {t.admin.dashboard.sinActividad}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Block 4: System Health (real) ── */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <h2 style={{ fontSize: 14, fontWeight: 600, color: '#0E243E', margin: 0 }}>
            {t.admin.dashboard.saludSistema}
          </h2>
          {isPlatformAdmin && (
            <AdminBadge bg="#FEF3C7" color="#92400E">
              SUPERADMIN
            </AdminBadge>
          )}
          {health?.totalLatency !== undefined && (
            <span style={{ fontSize: 10, color: '#9CA3AF', fontFamily: 'monospace' }}>
              total {health.totalLatency}ms
            </span>
          )}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 14 }}>
          <HealthCard
            name="PostgreSQL"
            detail="Supabase"
            status={health?.database ?? 'error'}
            latency={health?.dbLatency}
          />
          <HealthCard
            name="Meilisearch"
            detail="Indexacion"
            status={health?.meilisearch ?? 'not_configured'}
            latency={health?.meiliLatency}
          />
          <HealthCard
            name="Storage"
            detail="Documentos"
            status={health?.storage ?? 'not_configured'}
          />
          <HealthCard name="Auth" detail="JWT + bcrypt" status={health?.auth ?? 'ok'} />
          <HealthCard name="API IA" detail="Anthropic" status={health?.ia ?? 'not_configured'} />
        </div>
        {isPlatformAdmin && (
          <div style={{ marginTop: 12 }}>
            <button
              onClick={handleReindex}
              disabled={reindexing}
              style={{
                padding: '7px 18px',
                fontSize: 11,
                fontWeight: 600,
                background: reindexing ? '#9CA3AF' : '#2F5496',
                color: '#fff',
                border: 'none',
                borderRadius: 4,
                cursor: reindexing ? 'not-allowed' : 'pointer',
              }}
            >
              {reindexing
                ? 'Reindexando...'
                : (t.admin.dashboard.reindexar ?? 'Reindexar Meilisearch')}
            </button>
          </div>
        )}
      </div>

      {/* Toast */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            top: 16,
            right: 16,
            zIndex: 50,
            borderRadius: 8,
            padding: '12px 16px',
            fontSize: 14,
            fontWeight: 500,
            color: '#fff',
            backgroundColor: toast.type === 'warn' ? '#F59E0B' : '#059669',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          }}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
}

// 0571: HealthCard reusable con semáforo según status
function HealthCard({
  name,
  detail,
  status,
  latency,
}: {
  name: string;
  detail: string;
  status: 'ok' | 'error' | 'not_configured';
  latency?: number;
}) {
  const colors = {
    ok: { bar: '#15803D', dot: '#15803D', label: 'OK' },
    error: { bar: '#DC2626', dot: '#DC2626', label: 'ERROR' },
    not_configured: { bar: '#9CA3AF', dot: '#9CA3AF', label: 'N/A' },
  };
  const c = colors[status];
  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 8,
        border: '1px solid #D9D9D9',
        borderLeft: `3px solid ${c.bar}`,
        padding: '12px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
      }}
    >
      <div
        style={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          background: c.dot,
          flexShrink: 0,
        }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#1F3864' }}>{name}</div>
        <div style={{ fontSize: 10, color: '#595959' }}>
          {detail}
          {latency !== undefined && status === 'ok' && (
            <span style={{ fontFamily: 'monospace', marginLeft: 4 }}>· {latency}ms</span>
          )}
        </div>
      </div>
    </div>
  );
}
